/**
 * Raw macro package (`.pcmraw` when zipped): one immutable recording written by Playjector.
 * `manifest.json` is written last and is the commit marker for the directory.
 */
import { z } from 'zod';
import { sha256Hex, sha256HexSchema } from './canonicalJson';
import { macroInputModePolicySchema } from './inputModePolicy';
import { parseJsonLines, validateRawInputStream } from './rawInputEvent';
import { captureIntegritySchema } from './pipelineContracts';
import { MACRO_PACKAGE_LIMITS } from './limits';

export const RAW_PACKAGE_FILES = { manifest: 'manifest.json', policy: 'policy.json', video: 'video.mp4',
  frames: 'frames.jsonl', inputs: 'inputs.jsonl', regions: 'regions/index.json' } as const;

const fileRef = z.object({ file: z.string().min(1).max(256), sha256: sha256HexSchema, bytes: z.number().int().nonnegative() }).strict();
const intRect = z.object({ left: z.number().int(), top: z.number().int(), right: z.number().int(), bottom: z.number().int() }).strict();

export const rawScreenStatesSchema = z.array(z.object({ id: z.string().uuid(), name: z.string().min(1).max(80),
  tUs: z.number().int().nonnegative().safe(), frameSeq: z.number().int().nonnegative().safe(),
  regionIds: z.array(z.string().uuid()).max(64) }).strict()).max(4_096);

const rawPackageManifestObjectSchema = z.object({
  format: z.literal('playcast.macro.raw'), formatVersion: z.literal(2, {
    errorMap: () => ({ message: 'legacy_macro_requires_rerecording: raw v2 is required' }) }),
  durationUs: z.number().int().nonnegative().safe().max(MACRO_PACKAGE_LIMITS.rawMaxDurationUs),
  integrity: captureIntegritySchema,
  packageId: z.string().uuid(), campaignId: z.string().min(1).max(128), name: z.string().min(1).max(80),
  createdAtUnixMs: z.number().int().positive(),
  recorder: z.object({ editorVersion: z.string().max(64), playjectorVersion: z.string().max(64) }).strict(),
  clock: z.object({ source: z.literal('qpc'), ticksPerSecond: z.number().int().positive(),
    originQpcTicks: z.string().regex(/^\d{1,20}$/) }).strict(),
  capture: z.object({ source: z.enum(['desktop', 'editor']), rect: intRect,
    width: z.number().int().positive().max(16_384), height: z.number().int().positive().max(16_384),
    monitorIsPrimary: z.boolean(),
    /** Host inventory identity of the display/window pinned when recording was armed. */
    selectionId: z.string().min(1).max(256).optional() }).strict(),
  video: fileRef.extend({ codec: z.enum(['h264', 'hevc']), width: z.number().int().positive(),
    height: z.number().int().positive(), frameCount: z.number().int().nonnegative(), durationUs: z.number().int().nonnegative() }),
  frames: fileRef.extend({ frameCount: z.number().int().nonnegative() }),
  inputs: fileRef.extend({ eventCount: z.number().int().nonnegative() }),
  regions: fileRef.extend({ regionCount: z.number().int().nonnegative() }),
  policy: fileRef,
  screenStates: rawScreenStatesSchema,
}).strict();
export const rawPackageManifestSchema = rawPackageManifestObjectSchema.superRefine((manifest, context) => {
  const integrity = manifest.integrity;
  if (integrity.writtenInputEvents !== manifest.inputs.eventCount
    || integrity.encodedVideoFrames !== manifest.video.frameCount || integrity.indexedVideoFrames !== manifest.frames.frameCount
    || (manifest.capture.source === 'desktop' && (integrity.admittedEditorPackets !== 0 || integrity.appliedEditorPackets !== 0))) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['integrity'], message: 'recording_integrity_failed: manifest counters disagree' });
  }
  if (manifest.screenStates.some(state => state.tUs > manifest.durationUs)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['screenStates'], message: 'Screen states exceed sealed recording duration' });
  }
});
export type RawPackageManifest = z.infer<typeof rawPackageManifestSchema>;
export type RawPackageFileRef = z.infer<typeof fileRef>;

export const rawFrameRowSchema = z.object({ seq: z.number().int().nonnegative(),
  ptsUs: z.number().int().nonnegative(), tUs: z.number().int().nonnegative(), keyframe: z.boolean() }).strict();
export type RawFrameRow = z.infer<typeof rawFrameRowSchema>;

export const rawRegionFrameSchema = z.object({ frameSeq: z.number().int().nonnegative(), offsetUs: z.number().int(),
  role: z.enum(['anchor', 'temporal', 'negative']), file: z.string().min(1).max(256),
  sha256: sha256HexSchema, width: z.number().int().positive(), height: z.number().int().positive() }).strict();
export type RawRegionFrame = z.infer<typeof rawRegionFrameSchema>;

export const rawRegionRectSchema = z.object({ x: z.number().int().nonnegative(), y: z.number().int().nonnegative(),
  width: z.number().int().positive(), height: z.number().int().positive() }).strict();

export const rawRegionSchema = z.object({
  regionId: z.string().uuid(), screenStateId: z.string().uuid(), rect: rawRegionRectSchema,
  frameGroup: z.array(rawRegionFrameSchema).min(1).max(64),
}).strict().superRefine((region, context) => {
  const anchors = region.frameGroup.filter(frame => frame.role === 'anchor');
  if (anchors.length !== 1 || anchors[0]?.offsetUs !== 0) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['frameGroup'],
      message: 'Frame group needs exactly one anchor at offset 0' });
  }
});
export type RawRegion = z.infer<typeof rawRegionSchema>;

export const rawRegionIndexSchema = z.object({ regions: z.array(rawRegionSchema).max(1_024) }).strict();
export type RawRegionIndex = z.infer<typeof rawRegionIndexSchema>;

/** Minimal byte source `validateRawPackage` needs; `RawPackageReader` (MacroCompiler) satisfies it. */
export interface RawPackageFileSource { fileBytes(path: string): Promise<Uint8Array> }
export interface RawPackageValidation { ok: boolean; manifest?: RawPackageManifest; issues: string[] }

/** Checks manifest ↔ file digests ↔ row counts ↔ region references. Never throws for bad content. */
export async function validateRawPackage(source: RawPackageFileSource): Promise<RawPackageValidation> {
  const issues: string[] = [];
  const manifestBytes = await readOrNull(source, RAW_PACKAGE_FILES.manifest);
  const manifestParse = manifestBytes ? rawPackageManifestSchema.safeParse(parseJson(manifestBytes)) : null;
  if (!manifestParse?.success) return { ok: false, issues: ['manifest.json missing or schema-invalid'] };
  const manifest = manifestParse.data;
  const contents = new Map<string, Uint8Array>();
  for (const [key, ref] of Object.entries({ video: manifest.video, frames: manifest.frames, inputs: manifest.inputs,
    regions: manifest.regions, policy: manifest.policy })) {
    const bytes = await verifyFile(source, ref, issues);
    if (bytes) contents.set(key, bytes);
  }
  checkFrames(manifest, contents.get('frames'), issues);
  checkInputs(manifest, contents.get('inputs'), issues);
  checkPolicy(manifest, contents.get('policy'), issues);
  await checkRegions(source, manifest, contents.get('regions'), issues);
  return { ok: issues.length === 0, manifest, issues };
}

async function readOrNull(source: RawPackageFileSource, path: string): Promise<Uint8Array | null> {
  try { return await source.fileBytes(path); } catch { return null; }
}

function parseJson(bytes: Uint8Array): unknown {
  try { return JSON.parse(new TextDecoder().decode(bytes)) as unknown; } catch { return undefined; }
}

function parseLines(bytes: Uint8Array): unknown[] | null {
  try { return parseJsonLines(new TextDecoder().decode(bytes)); } catch { return null; }
}

async function verifyFile(source: RawPackageFileSource, ref: RawPackageFileRef, issues: string[]): Promise<Uint8Array | null> {
  const bytes = await readOrNull(source, ref.file);
  if (!bytes) { issues.push(`${ref.file}: missing`); return null; }
  if (bytes.byteLength !== ref.bytes) issues.push(`${ref.file}: size ${bytes.byteLength} != ${ref.bytes}`);
  if (await sha256Hex(bytes) !== ref.sha256) issues.push(`${ref.file}: digest mismatch`);
  return bytes;
}

function checkFrames(manifest: RawPackageManifest, bytes: Uint8Array | undefined, issues: string[]): void {
  if (!bytes) return;
  const rows = parseLines(bytes);
  if (!rows || !rows.every(row => rawFrameRowSchema.safeParse(row).success)) { issues.push('frames.jsonl: schema-invalid'); return; }
  if (rows.length !== manifest.frames.frameCount) issues.push('frames.jsonl: row count != manifest.frames.frameCount');
  if (manifest.video.frameCount !== manifest.frames.frameCount) issues.push('video.frameCount != frames.frameCount');
}

function checkInputs(manifest: RawPackageManifest, bytes: Uint8Array | undefined, issues: string[]): void {
  if (!bytes) return;
  const rows = parseLines(bytes);
  if (!rows) { issues.push('inputs.jsonl: not JSONL'); return; }
  const stream = validateRawInputStream(rows);
  for (const issue of stream.issues) issues.push(`inputs.jsonl[${issue.index}]: ${issue.code}`);
  if (rows.length !== manifest.inputs.eventCount) issues.push('inputs.jsonl: row count != manifest.inputs.eventCount');
}

function checkPolicy(manifest: RawPackageManifest, bytes: Uint8Array | undefined, issues: string[]): void {
  if (!bytes) return;
  const policy = macroInputModePolicySchema.safeParse(parseJson(bytes));
  if (!policy.success) { issues.push('policy.json: schema-invalid'); return; }
  if (policy.data.captureSource !== manifest.capture.source) issues.push('policy.captureSource != capture.source');
}

async function checkRegions(source: RawPackageFileSource, manifest: RawPackageManifest,
  bytes: Uint8Array | undefined, issues: string[]): Promise<void> {
  if (!bytes) return;
  const index = rawRegionIndexSchema.safeParse(parseJson(bytes));
  if (!index.success) { issues.push('regions/index.json: schema-invalid'); return; }
  if (index.data.regions.length !== manifest.regions.regionCount) issues.push('regions: count != manifest.regions.regionCount');
  const screenStates = new Set(manifest.screenStates.map(state => state.id));
  for (const region of index.data.regions) {
    if (!screenStates.has(region.screenStateId)) issues.push(`region ${region.regionId}: unknown screen state`);
    for (const frame of region.frameGroup) {
      if (frame.frameSeq >= manifest.frames.frameCount) issues.push(`${frame.file}: frameSeq out of range`);
      const frameBytes = await readOrNull(source, frame.file);
      if (!frameBytes) issues.push(`${frame.file}: missing`);
      else if (await sha256Hex(frameBytes) !== frame.sha256) issues.push(`${frame.file}: digest mismatch`);
    }
  }
}
